<script lang="ts">
  import Form from "../ui/Form.svelte"
  import {
    Milestone,
    isFixed,
    formatPercentage,
    validateMilestone,
    isSolePersistedPercentageMilestone,
    toggleType,
    canToggleToFixed,
    sortMilestonesByDate,
    LateRegistrantPaymentStrategy,
  } from "@models/milestones/Milestone"
  import { formatISO, parseISO } from "date-fns"
  import { post, put } from "@util/api"
  import AmountInput from "@components/ui/AmountInput.svelte"
  import ErrorMessage from "@components/ui/ErrorMessage.svelte"
  import SubmitButton from "@components/ui/SubmitButton.svelte"
  import MilestoneLabel from "./MilestoneLabel.svelte"
  export let milestone: Milestone
  export let milestones: Milestone[]
  export let path: string
  export let registrationClosesOn: string
  export let lateRegistrantPaymentStrategy: LateRegistrantPaymentStrategy = "catch_up"
  export let index: number = 0

  $: canBeFixed = canToggleToFixed(milestones, milestone, lateRegistrantPaymentStrategy)

  function validate(data: Milestone) {
    const base = validateMilestone(data)
    if (!canBeFixed && data.type === "fixed") {
      return {
        ...base,
        date: "Only the first payment by date can be a fixed amount with the distribute strategy. Change the date or switch to percentage.",
      }
    }
    return base
  }

  async function submit() {
    let result
    if (milestone.id) {
      result = await put(path, { milestone })
    } else {
      result = await post(path, { milestone })
    }

    if (result.ok) {
      milestone = { ...result.body.data.milestoneDate, isEditing: false }
      milestones = sortMilestonesByDate(milestones)
    }

    return result
  }

  function setDateValue(e) {
    // the date-picker-with-presets component has a hidden date and an internal input
    // we want to grab the value of the hidden input not the internal input which
    // is used purely for display purposes
    if (e.target.dataset.role !== "internal-input") {
      let date = e.target.value
      milestone.date = date && formatISO(parseISO(date), { representation: "date" })
    }
  }
</script>

<Form data={milestone} {validate} {submit} let:submitted let:submitting let:errors>
  <div data-role={`milestone-edit-${milestone.id}`} class="pl-4 py-1 grid grid-cols-7 gap-4 items-start" data-path={path}>
    <div class="col-span-2" class:mt-2={milestone.isEditing}>
      <MilestoneLabel {milestones} {milestone} />
    </div>
    {#if isSolePersistedPercentageMilestone(milestones, milestone)}
      <div />
    {:else if !canBeFixed && milestone.type !== "fixed"}
      <div class="mt-2 px-4 text-gray-500" title="Distribute strategy requires all non-deposit payments to be percentage-based">%</div>
    {:else}
      <select data-role="milestone-type" value={milestone.type} on:change={() => (milestone = toggleType(milestone))}>
        <option value="fixed">$</option>
        <option value="percentage">%</option>
      </select>
    {/if}
    <div class="">
      {#if isFixed(milestone)}
        <AmountInput name="amount" bind:value={milestone.amount} />
      {:else}
        <div class="mt-2 px-4">{formatPercentage(milestones)}%</div>
      {/if}
      <ErrorMessage when={submitted} error={errors.amount} />
    </div>
    <div class="col-span-2" data-role="milestone-date">
      <date-picker-with-presets
        value={milestone.date}
        label="When registration ends"
        relative-to-date={registrationClosesOn}
        on:change={(e) => {
          setDateValue(e)
        }}
        is-valid={errors.date === undefined} />
      <ErrorMessage when={submitted} error={errors.date} />
    </div>
    <SubmitButton {submitting}>Save</SubmitButton>
  </div>
</Form>
